import { useQueryClient } from '@tanstack/react-query';
import ConfirmationModal from '@/components/ui-kit/confirmation-modal';
import { Dialog } from '@/components/ui-kit/dialog';
import { showErrorToast, showSuccessToast } from '@/hooks/use-toast';
import { useDeleteTool, useToolAssociatedAgentCount } from '@/modules/gpt-chats/hooks/use-tools';
import { ErrorTransformer } from '@/modules/gpt-chats/utils/error-transform';
import { IAPIError } from '@/modules/gpt-chats/types/tools.service.type';
import { useTranslation } from 'react-i18next';

type DeleteToolProps = {
  toolId: string;
  isBlocksTool?: boolean;
  open: boolean;
  onOpenChange: (value: boolean) => void;
};
export const DeleteTool = ({ toolId, isBlocksTool, open, onOpenChange }: DeleteToolProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutateAsync: deleteTool, isPending } = useDeleteTool();
  const { refetch: fetchAssociatedAgentCount, isFetching: isAgentCountFetching } =
    useToolAssociatedAgentCount(toolId, false);

  const confirmHandler = async () => {
    try {
      const { data: agentCountResponse } = await fetchAssociatedAgentCount();

      if (((agentCountResponse as any)?.data as number) > 0) {
        showErrorToast({ errors: t('TOOL_ASSOCIATED_WITH_AGENT') });
        return;
      }

      const res = await deleteTool({ toolId, isBlocksTool });
      // 204 No Content means success but empty response body
      if (!res || res.is_success !== false) {
        showSuccessToast({ description: t('TOOL_DELETED_SUCCESSFULLY') });
        queryClient.invalidateQueries({ queryKey: ['tools'] });
        onOpenChange(false);
      } else {
        showErrorToast({ errors: res.message || t('FAILED_TO_DELETE_TOOL') });
      }
    } catch (error: unknown) {
      const apiError = error as IAPIError;
      if (apiError?.detail && Array.isArray(apiError.detail)) {
        const errorMessages = apiError.detail.map((err) => err.msg).join(', ');
        showErrorToast({ errors: errorMessages });
      } else {
        showErrorToast({ errors: ErrorTransformer(error) });
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <ConfirmationModal
        data={{
          dialogTitle: t('DELETE_TOOL_TITLE'),
          dialogSubtitle: t('DELETE_TOOL_SUBTITLE'),
        }}
        onConfirm={confirmHandler}
        onCancel={() => onOpenChange(false)}
        buttonState={{ confirm: { disable: isPending || isAgentCountFetching } }}
      />
    </Dialog>
  );
};
